import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../../hooks/usePageTitle';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  Chip, List, ListItem, ListItemButton, ListItemText, Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Layout from '../../components/Layout';
import adminService from '../../services/adminService';
import { useUnion } from '../../contexts/UnionContext';

const roleColor = {
  ADMIN: 'error',
  ACCOUNTANT: 'warning',
  BOARD_MEMBER: 'info',
  OWNER: 'primary',
  RESIDENT: 'default',
};

function UnionMembersAdmin() {
  const { t } = useTranslation();

  const ROLES = [
    { value: 'ADMIN', label: t('members.roleAdmin') },
    { value: 'ACCOUNTANT', label: t('members.roleAccountant') },
    { value: 'BOARD_MEMBER', label: t('members.roleBoardMember') },
    { value: 'OWNER', label: t('members.roleOwner') },
    { value: 'RESIDENT', label: t('members.roleResident') },
  ];

  usePageTitle(t('nav.members'));
  const { activeUnion } = useUnion();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('RESIDENT');
  const [showUserResults, setShowUserResults] = useState(false);
  const userSearchTimeout = useRef(null);
  const userBoxRef = useRef(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editActive, setEditActive] = useState(true);

  useEffect(() => {
    if (activeUnion) loadMembers();
  }, [activeUnion]);

  useEffect(() => {
    const handleClick = (e) => {
      if (userBoxRef.current && !userBoxRef.current.contains(e.target)) {
        setShowUserResults(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getMemberships();
      setMembers(data);
      setError('');
    } catch (err) {
      setError(t('members.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleUserQueryChange = (e) => {
    const val = e.target.value;
    setUserQuery(val);
    setSelectedUser(null);
    clearTimeout(userSearchTimeout.current);
    if (val.length < 2) { setUserResults([]); setShowUserResults(false); return; }
    userSearchTimeout.current = setTimeout(async () => {
      setUserSearching(true);
      try {
        const results = await adminService.searchUsers(val);
        setUserResults(results);
        setShowUserResults(results.length > 0);
      } catch { setUserResults([]); }
      finally { setUserSearching(false); }
    }, 300);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserQuery(user.username + (user.full_name ? ` (${user.full_name})` : ''));
    setShowUserResults(false);
  };

  const handleAdd = async () => {
    if (!selectedUser) { setError('Please select a user from the list'); return; }
    try {
      await adminService.addMembership({ user: selectedUser.id, role: newRole });
      setAddOpen(false);
      setUserQuery(''); setSelectedUser(null); setNewRole('RESIDENT');
      loadMembers();
    } catch (err) {
      setError(err.response?.data?.non_field_errors?.[0] || t('members.failedAdd'));
    }
  };

  const handleOpenEdit = (member) => {
    setEditMember(member);
    setEditRole(member.role);
    setEditActive(member.is_active);
    setEditOpen(true);
  };

  const handleEdit = async () => {
    try {
      await adminService.updateMembership(editMember.id, { role: editRole, is_active: editActive });
      setEditOpen(false);
      loadMembers();
    } catch {
      setError(t('members.failedUpdate'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('members.removeConfirm'))) return;
    try {
      await adminService.deleteMembership(id);
      loadMembers();
    } catch {
      setError(t('members.failedRemove'));
    }
  };

  if (!activeUnion) {
    return (
      <Layout>
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">{t('members.selectUnion')}</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4">{t('members.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{activeUnion.name}</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setError(''); setAddOpen(true); }}>
            {t('members.addMember')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('members.username')}</TableCell>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('common.email')}</TableCell>
                  <TableCell>{t('members.role')}</TableCell>
                  <TableCell align="center">{t('common.active')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 2 }}>{t('members.noMembers')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {members.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell><strong>{m.user_username}</strong></TableCell>
                    <TableCell>{m.user_full_name || '—'}</TableCell>
                    <TableCell>{m.user_email}</TableCell>
                    <TableCell>
                      <Chip
                        label={ROLES.find(r => r.value === m.role)?.label || m.role}
                        color={roleColor[m.role] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={m.is_active ? t('common.active') : t('common.inactive')}
                        color={m.is_active ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(m)}><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(m.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Member Dialog */}
        <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('members.addMember')}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box ref={userBoxRef} sx={{ position: 'relative', mb: 2 }}>
              <TextField
                fullWidth
                label={t('members.searchByUsername')}
                value={userQuery}
                onChange={handleUserQueryChange}
                onFocus={() => userResults.length > 0 && setShowUserResults(true)}
                autoComplete="off"
                InputProps={{
                  endAdornment: userSearching ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null,
                }}
              />
              {showUserResults && (
                <Paper elevation={4} sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300, maxHeight: 200, overflowY: 'auto', mt: 0.5 }}>
                  <List dense disablePadding>
                    {userResults.map((u) => (
                      <ListItem key={u.id} disablePadding>
                        <ListItemButton onMouseDown={() => handleSelectUser(u)}>
                          <ListItemText primary={u.username} secondary={u.email} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
            <FormControl fullWidth>
              <InputLabel>{t('members.role')}</InputLabel>
              <Select value={newRole} onChange={(e) => setNewRole(e.target.value)} label={t('members.role')}>
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAdd} variant="contained" disabled={!selectedUser}>{t('members.add')}</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Member Dialog */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('members.editMember', { username: editMember?.user_username })}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('members.role')}</InputLabel>
              <Select value={editRole} onChange={(e) => setEditRole(e.target.value)} label={t('members.role')}>
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />}
              label={t('common.active')}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleEdit} variant="contained">{t('common.save')}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default UnionMembersAdmin;
